import { useEffect, useState } from 'react';
import {
  getUser, updateUser, deleteUser, grantAdmin, revokeAdmin,
  grantOperator, revokeOperator, lockChat, unlockChat,
  setBattery, adjustBattery, getBatteryHistory,
  getUserTypeLabel, getUserTypeBadgeColor,
  findUserByEmail, searchUsersByNickname,
} from '../../services/userService';
import { getUserDMs, sendDM } from '../../services/dmService';
import { useAuthStore } from '../../stores/authStore';
import { formatTimestamp, isUserActiveNow } from '../../utils/time';
import { coordsToFrequency } from '../../utils/frequency';
import type { User, BatteryTransaction, DirectMessage } from '../../types';

interface Props {
  uid: string;
  onClose: () => void;
  onDeleted?: () => void;
}

export default function UserDetailModal({ uid, onClose, onDeleted }: Props) {
  const { adminUser } = useAuthStore();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'profile' | 'info' | 'battery' | 'dm' | 'sendDm'>('profile');
  const [batteryHistory, setBatteryHistoryData] = useState<BatteryTransaction[]>([]);
  const [batteryInput, setBatteryInput] = useState('');
  const [dms, setDms] = useState<{ inbox: DirectMessage[]; sent: DirectMessage[] }>({ inbox: [], sent: [] });
  const [dmTab, setDmTab] = useState<'inbox' | 'sent'>('inbox');
  const [dmContent, setDmContent] = useState('');

  // DM sender selection
  const [dmSenderEmail, setDmSenderEmail] = useState('');
  const [dmSenderNickname, setDmSenderNickname] = useState('');
  const [dmSenderId, setDmSenderId] = useState('');
  const [dmSenderResolved, setDmSenderResolved] = useState('');
  const [dmNicknameResults, setDmNicknameResults] = useState<User[]>([]);
  const [showDmNicknameDropdown, setShowDmNicknameDropdown] = useState(false);

  useEffect(() => { loadUser(); }, [uid]);

  // Default DM sender to admin
  useEffect(() => {
    if (adminUser && !dmSenderId) {
      setDmSenderEmail(adminUser.email);
      setDmSenderNickname(adminUser.nickname);
      setDmSenderId(adminUser.uid);
      setDmSenderResolved(`${adminUser.nickname} (${adminUser.email})`);
    }
  }, [adminUser]);

  async function loadUser() {
    setLoading(true);
    try { setUser(await getUser(uid)); }
    finally { setLoading(false); }
  }

  async function loadBattery() {
    setBatteryHistoryData(await getBatteryHistory(uid));
  }

  async function loadDMs() {
    setDms(await getUserDMs(uid));
  }

  useEffect(() => {
    if (tab === 'battery') loadBattery();
    if (tab === 'dm') loadDMs();
  }, [tab]);

  async function handleAdjust(delta: number) {
    if (!batteryInput) return;
    const amount = parseInt(batteryInput);
    if (isNaN(amount) || amount <= 0) return;
    await adjustBattery(uid, delta > 0 ? amount : -amount, adminUser?.uid || 'admin');
    setBatteryInput('');
    loadUser();
    if (tab === 'battery') loadBattery();
  }

  async function handleSetAbsolute() {
    if (!batteryInput) return;
    const amount = parseInt(batteryInput);
    if (isNaN(amount) || amount < 0) return;
    await setBattery(uid, amount, adminUser?.uid || 'admin');
    setBatteryInput('');
    loadUser();
    if (tab === 'battery') loadBattery();
  }

  async function handleDelete() {
    if (!confirm(`"${user?.nickname}" 사용자를 삭제하시겠습니까?`)) return;
    await deleteUser(uid);
    onDeleted?.();
    onClose();
  }

  // ─── DM Sender selection ───
  function applyDmSender(u: User) {
    setDmSenderId(u.uid);
    setDmSenderEmail(u.email);
    setDmSenderNickname(u.nickname);
    setDmSenderResolved(`${u.nickname} (${u.email})`);
    setShowDmNicknameDropdown(false);
  }

  async function handleDmApplyByEmail() {
    if (!dmSenderEmail) return;
    const u = await findUserByEmail(dmSenderEmail);
    if (u) applyDmSender(u);
    else alert(`"${dmSenderEmail}" 이메일의 사용자를 찾을 수 없습니다.`);
  }

  async function handleDmFindByNickname() {
    if (!dmSenderNickname) return;
    const results = await searchUsersByNickname(dmSenderNickname);
    if (results.length === 0) {
      alert(`"${dmSenderNickname}"을(를) 포함하는 닉네임의 사용자를 찾을 수 없습니다.`);
      setShowDmNicknameDropdown(false);
    } else if (results.length === 1) {
      applyDmSender(results[0]);
    } else {
      setDmNicknameResults(results);
      setShowDmNicknameDropdown(true);
    }
  }

  async function handleSendDM() {
    if (!dmContent.trim() || !dmSenderId || !user) return;
    await sendDM({
      senderId: dmSenderId,
      senderNickname: dmSenderNickname,
      recipientId: user.uid,
      recipientNickname: user.nickname,
      content: dmContent,
    });
    setDmContent('');
    setTab('dm');
    loadDMs();
  }

  async function handleToggle(action: () => Promise<void>) {
    await action();
    loadUser();
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl p-8" onClick={e => e.stopPropagation()}>로딩 중...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl p-8" onClick={e => e.stopPropagation()}>사용자를 찾을 수 없습니다.</div>
      </div>
    );
  }

  const freq = user.lat != null && user.lng != null ? coordsToFrequency(user.lat, user.lng) : 'N/A';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user.profileImageUrl && <img src={user.profileImageUrl} className="w-10 h-10 rounded-full object-cover" />}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-lg">{user.nickname}</h2>
                <span className={`px-2 py-0.5 text-xs rounded-full ${getUserTypeBadgeColor(user)}`}>{getUserTypeLabel(user)}</span>
                {user.chatLocked && <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">채팅잠금</span>}
                {isUserActiveNow(user.lastActiveAt) && <span className="w-2 h-2 bg-green-500 rounded-full inline-block" />}
              </div>
              <p className="text-xs text-gray-500">{user.email} | {user.uid}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-4">
          {(['profile', 'info', 'battery', 'dm', 'sendDm'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition ${tab === t ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}
            >
              {t === 'profile' ? '프로필' : t === 'info' ? '정보' : t === 'battery' ? '배터리' : t === 'dm' ? '쪽지 내역' : '쪽지 보내기'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'profile' && (
            <ProfileTab user={user} onUpdate={(u) => setUser(u)} />
          )}
          {tab === 'info' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-sm mb-2">기본 정보</h3>
                <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                  {([
                    ['이메일', user.email],
                    ['닉네임', user.nickname],
                    ['성별', user.gender === 'male' ? '남성' : user.gender === 'female' ? '여성' : '미지정'],
                    ['관리자 여부', user.isAdmin ? '예' : '아니오'],
                    ['운영자 여부', user.isOperator ? '예' : '아니오'],
                    ['봇 여부', user.isBot ? '예' : '아니오'],
                    ['채팅 상태', user.chatLocked ? '잠금' : '정상'],
                  ] as [string, string][]).map(([k, v]) => (
                    <div key={k} className="flex"><span className="text-gray-500 w-28">{k}:</span><span className="font-medium">{v}</span></div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-sm mb-2">세션/인증 정보</h3>
                <div className="grid grid-cols-1 gap-y-1 text-sm">
                  <div><span className="text-gray-500">Auth UID:</span> <span className="font-mono text-xs">{user.uid}</span></div>
                  <div><span className="text-gray-500">senderId:</span> <span className="font-mono text-xs">{user.uid}</span></div>
                  <div><span className="text-gray-500">세션 장치:</span> {user.activeSessionDevice || 'N/A'}</div>
                  <div><span className="text-gray-500">세션 시작:</span> {formatTimestamp(user.activeSessionAt)}</div>
                  <div><span className="text-gray-500">최근 로그인:</span> {formatTimestamp(user.lastActiveAt)}</div>
                  <div><span className="text-gray-500">생성일:</span> {formatTimestamp(user.createdAt)}</div>
                  <div><span className="text-gray-500">최근 업데이트:</span> {formatTimestamp(user.updatedAt)}</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-sm mb-2">위치 정보</h3>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div><span className="text-gray-500">위도:</span> {user.lat?.toFixed(6) ?? 'N/A'}</div>
                  <div><span className="text-gray-500">경도:</span> {user.lng?.toFixed(6) ?? 'N/A'}</div>
                  <div><span className="text-gray-500">주파수:</span> <span className="font-mono">{freq}</span></div>
                  <div><span className="text-gray-500">반경:</span> {(user.radius || 0).toLocaleString()}m</div>
                  <div><span className="text-gray-500">수동주파수:</span> {user.manualFreqApplied ? user.manualFrequency || '적용중' : 'N/A'}</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-sm mb-2">알림/설정</h3>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div><span className="text-gray-500">푸시 활성:</span> {user.pushNotificationsEnabled ? '예' : '아니오'}</div>
                  <div><span className="text-gray-500">푸시 토큰:</span> {user.pushToken ? '있음' : 'N/A'}</div>
                  <div><span className="text-gray-500">언어:</span> {user.language || 'N/A'}</div>
                  <div><span className="text-gray-500">자기소개:</span> {user.introduction || 'N/A'}</div>
                  <div><span className="text-gray-500">배터리:</span> <span className="font-mono font-bold">{(user.battery || 0).toLocaleString()}</span> 셀</div>
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-sm mb-3">배터리 관리</h3>
                <div className="flex gap-2 items-center mb-4">
                  <span className="text-sm text-gray-500">셀 관리:</span>
                  <input type="number" value={batteryInput} onChange={e => setBatteryInput(e.target.value)} placeholder="수량" className="w-28 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <button onClick={() => handleAdjust(1)} className="px-3 py-1 text-sm bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">지급</button>
                  <button onClick={() => handleAdjust(-1)} className="px-3 py-1 text-sm bg-orange-50 text-orange-700 border border-orange-200 rounded hover:bg-orange-100">차감</button>
                  <button onClick={handleSetAbsolute} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100">절대값변경</button>
                </div>

                <h3 className="font-semibold text-sm mb-3">권한/상태 관리</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {user.chatLocked
                    ? <button onClick={() => handleToggle(() => unlockChat(uid))} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 border border-green-200 rounded">채팅 잠금 해제</button>
                    : <button onClick={() => handleToggle(() => lockChat(uid))} className="px-3 py-1.5 text-sm bg-yellow-50 text-yellow-700 border border-yellow-200 rounded">채팅 차단</button>
                  }
                  <button onClick={handleDelete} className="px-3 py-1.5 text-sm bg-red-50 text-red-700 border border-red-200 rounded">사용자 삭제</button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {user.isAdmin
                    ? <button onClick={() => handleToggle(() => revokeAdmin(uid))} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded">관리자 해제</button>
                    : <button onClick={() => handleToggle(() => grantAdmin(uid))} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded">관리자 임명</button>
                  }
                  {user.isOperator
                    ? <button onClick={() => handleToggle(() => revokeOperator(uid))} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded">운영자 해제</button>
                    : <button onClick={() => handleToggle(() => grantOperator(uid))} className="px-3 py-1.5 text-sm bg-purple-50 text-purple-600 border border-purple-200 rounded">운영자 임명</button>
                  }
                </div>

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setTab('battery')} className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded">배터리 내역</button>
                  <button onClick={() => setTab('sendDm')} className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded">쪽지 보내기</button>
                  <button onClick={() => setTab('dm')} className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded">쪽지 내역 보기</button>
                  {user.profileImageUrl && (
                    <a href={user.profileImageUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded hover:bg-gray-100">프로필 보기</a>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'battery' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold">배터리 이력 ({batteryHistory.length}건)</span>
                <button onClick={loadBattery} className="text-sm text-blue-600 hover:underline">새로고침</button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">유형</th>
                    <th className="px-3 py-2 text-right">금액</th>
                    <th className="px-3 py-2 text-right">잔액</th>
                    <th className="px-3 py-2 text-left">시간</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {batteryHistory.map((tx, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5">{tx.type}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>{tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-gray-500">{tx.balance != null ? tx.balance.toLocaleString() : '-'}</td>
                      <td className="px-3 py-1.5 text-gray-400">{new Date(tx.timestamp).toLocaleString('ko-KR')}</td>
                    </tr>
                  ))}
                  {batteryHistory.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-400">이력 없음</td></tr>}
                </tbody>
              </table>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-semibold mb-1">현재 배터리: <span className="font-mono">{(user.battery || 0).toLocaleString()}</span> 셀</p>
                <div className="flex gap-2 items-center mt-2">
                  <input type="number" value={batteryInput} onChange={e => setBatteryInput(e.target.value)} placeholder="수량" className="w-28 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <button onClick={() => handleAdjust(1)} className="px-3 py-1 text-sm bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">지급</button>
                  <button onClick={() => handleAdjust(-1)} className="px-3 py-1 text-sm bg-orange-50 text-orange-700 border border-orange-200 rounded hover:bg-orange-100">차감</button>
                  <button onClick={handleSetAbsolute} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100">절대값변경</button>
                </div>
              </div>
            </div>
          )}

          {tab === 'dm' && (
            <div>
              <div className="flex gap-3 mb-3">
                <button onClick={() => setDmTab('inbox')} className={`text-sm font-medium ${dmTab === 'inbox' ? 'text-blue-600' : 'text-gray-500'}`}>받은 쪽지 ({dms.inbox.length})</button>
                <button onClick={() => setDmTab('sent')} className={`text-sm font-medium ${dmTab === 'sent' ? 'text-blue-600' : 'text-gray-500'}`}>보낸 쪽지 ({dms.sent.length})</button>
                <button onClick={loadDMs} className="text-sm text-blue-600 hover:underline ml-auto">새로고침</button>
              </div>
              <div className="space-y-2">
                {(dmTab === 'inbox' ? dms.inbox : dms.sent).map(dm => (
                  <div key={dm.id} className="p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{dmTab === 'inbox' ? `보낸 사람: ${dm.senderNickname}` : `받는 사람: ${dm.recipientNickname}`}</span>
                      <span>{formatTimestamp(dm.sentAt, 'short')} {dm.isRead ? '(읽음)' : ''}</span>
                    </div>
                    <p>{dm.content}</p>
                  </div>
                ))}
                {(dmTab === 'inbox' ? dms.inbox : dms.sent).length === 0 && <p className="text-center text-gray-400 py-4">쪽지 없음</p>}
              </div>
            </div>
          )}

          {tab === 'sendDm' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">받는 사람: <strong>{user.nickname}</strong> ({user.email})</p>

              {/* Sender selection */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">발신자 설정</h4>
                <div className="flex items-center gap-1">
                  <label className="w-14 text-xs text-gray-500">이메일:</label>
                  <input value={dmSenderEmail} onChange={e => setDmSenderEmail(e.target.value)} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <button onClick={handleDmApplyByEmail} className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200">적용</button>
                </div>
                <div className="flex items-center gap-1 relative">
                  <label className="w-14 text-xs text-gray-500">닉네임:</label>
                  <input
                    value={dmSenderNickname}
                    onChange={e => { setDmSenderNickname(e.target.value); setShowDmNicknameDropdown(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleDmFindByNickname()}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <button onClick={handleDmFindByNickname} className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200">사용자 찾기</button>
                  {showDmNicknameDropdown && dmNicknameResults.length > 0 && (
                    <div className="absolute top-full left-14 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
                      {dmNicknameResults.map(u => (
                        <button key={u.uid} onClick={() => applyDmSender(u)}
                          className="w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0">
                          <span className="font-medium">{u.nickname}</span>
                          <span className="text-xs text-gray-400">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {dmSenderResolved && (
                  <p className="text-xs text-blue-600">발신자: {dmSenderResolved} <span className="text-gray-400 font-mono">({dmSenderId.substring(0, 12)}...)</span></p>
                )}
              </div>

              <textarea
                value={dmContent}
                onChange={e => setDmContent(e.target.value)}
                placeholder="쪽지 내용을 입력하세요..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
              />
              <button onClick={handleSendDM} disabled={!dmSenderId} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400">전송</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Profile Tab ───

function ProfileTab({ user, onUpdate }: { user: User; onUpdate: (u: User) => void }) {
  const [editingIntro, setEditingIntro] = useState(false);
  const [introValue, setIntroValue] = useState(user.introduction ?? '');
  const [uploading, setUploading] = useState(false);

  async function handleSaveIntro() {
    await updateUser(user.uid, { introduction: introValue.trim() } as Partial<User>);
    onUpdate({ ...user, introduction: introValue.trim() });
    setEditingIntro(false);
  }

  async function handleDeleteIntro() {
    if (!confirm('자기소개를 삭제하시겠습니까?')) return;
    await updateUser(user.uid, { introduction: '' } as Partial<User>);
    onUpdate({ ...user, introduction: '' });
    setIntroValue('');
  }

  async function handleUploadImage(files: FileList | null) {
    if (!files || !files[0]) return;
    setUploading(true);
    try {
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../../lib/firebase/app');
      // Crop to 512x512
      const blob = await cropToSquare(files[0]);
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateUser(user.uid, { profileImageUrl: url } as Partial<User>);
      onUpdate({ ...user, profileImageUrl: url });
    } catch (err) {
      alert('업로드 실패: ' + (err as Error).message);
    } finally { setUploading(false); }
  }

  async function handleDeleteImage() {
    if (!confirm('프로필 이미지를 삭제하시겠습니까?')) return;
    try {
      const { ref, deleteObject } = await import('firebase/storage');
      const { storage } = await import('../../lib/firebase/app');
      await deleteObject(ref(storage, `profileImages/${user.uid}`)).catch(() => {});
      await updateUser(user.uid, { profileImageUrl: '' } as Partial<User>);
      onUpdate({ ...user, profileImageUrl: '' });
    } catch (err) {
      alert('삭제 실패: ' + (err as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      {/* Profile Image */}
      <div className="text-center">
        <div className="inline-block relative">
          {user.profileImageUrl ? (
            <img src={user.profileImageUrl} className="w-32 h-32 rounded-full object-cover border-2 border-gray-200" />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-4xl border-2 border-gray-200">
              ?
            </div>
          )}
        </div>
        <div className="mt-3 flex justify-center gap-2">
          <label className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer ${uploading ? 'bg-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            {uploading ? '업로드 중...' : '이미지 변경'}
            <input type="file" accept="image/*" onChange={e => handleUploadImage(e.target.files)} className="hidden" disabled={uploading} />
          </label>
          {user.profileImageUrl && (
            <button onClick={handleDeleteImage} className="px-3 py-1.5 text-xs bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">
              이미지 삭제
            </button>
          )}
        </div>
      </div>

      {/* Introduction */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">자기소개</h3>
          <div className="flex gap-1">
            {!editingIntro ? (
              <>
                <button onClick={() => setEditingIntro(true)} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">편집</button>
                {user.introduction && <button onClick={handleDeleteIntro} className="px-2 py-0.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">삭제</button>}
              </>
            ) : (
              <>
                <button onClick={handleSaveIntro} className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">저장</button>
                <button onClick={() => { setEditingIntro(false); setIntroValue(user.introduction ?? ''); }} className="px-2 py-0.5 text-xs bg-gray-200 rounded hover:bg-gray-300">취소</button>
              </>
            )}
          </div>
        </div>
        {editingIntro ? (
          <textarea value={introValue} onChange={e => setIntroValue(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
        ) : (
          <p className="text-sm text-gray-700">{user.introduction || <span className="text-gray-400">자기소개가 없습니다.</span>}</p>
        )}
      </div>
    </div>
  );
}

function cropToSquare(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const size = 512;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      const minDim = Math.min(img.width, img.height);
      const sx = (img.width - minDim) / 2;
      const sy = (img.height - minDim) / 2;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Crop failed')),
        'image/png',
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
